import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { upsertMessage } from "@/hooks/useChat";
import { api } from "@/lib/api";

const WS_URL = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/chat/ws`;
const Ctx = createContext({ connected: false, typing: {}, sendTyping: () => {}, setActive: () => {} });

export const useChatSocket = () => useContext(Ctx);

export const ChatSocketProvider = ({ children }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [connected, setConnected] = useState(false);
  const [typing, setTyping] = useState({});
  const ws = useRef(null);
  const active = useRef(null);
  const where = useRef(pathname);
  const lastTyping = useRef({});
  where.current = pathname;

  const onEvent = useCallback((ev) => {
    if (ev.type === "typing") {
      setTyping((t) => ({ ...t, [ev.channel_id]: { ...t[ev.channel_id], [ev.user.id]: { name: ev.user.name, until: Date.now() + 4000 } } }));
      return;
    }
    if (ev.type !== "message" && ev.type !== "message_updated") return;
    const m = ev.message;
    upsertMessage(qc, m);
    qc.invalidateQueries({ queryKey: ["chat-channels"] });
    if (ev.type !== "message" || m.author.id === user?.id) return;
    setTyping((t) => { const c = { ...t[m.channel_id] }; delete c[m.author.id]; return { ...t, [m.channel_id]: c }; });
    if (where.current === "/chat" && active.current === m.channel_id) return;
    const channel = qc.getQueryData(["chat-channels"])?.find((c) => c.id === m.channel_id);
    toast(`${m.author.name} · #${channel?.name || "chat"}`, {
      description: (m.text || "📎 Allegato").slice(0, 120),
      action: { label: "Apri", onClick: () => navigate(`/chat?canale=${m.channel_id}`) },
    });
  }, [qc, user?.id, navigate]);

  useEffect(() => {
    if (!user) return undefined;
    let stop = false; let retry = 0; let timer; let ping;
    const connect = async () => {
      if (retry > 0) { try { await api.get("/auth/me"); } catch { /* il refresh è gestito dall'interceptor */ } }
      if (stop) return;
      const sock = new WebSocket(WS_URL);
      ws.current = sock;
      sock.onopen = () => {
        retry = 0; setConnected(true);
        qc.invalidateQueries({ queryKey: ["chat-channels"] });
        qc.invalidateQueries({ queryKey: ["chat-messages"] });
        ping = setInterval(() => { if (sock.readyState === 1) sock.send(JSON.stringify({ type: "ping" })); }, 25000);
      };
      sock.onmessage = (e) => { try { onEvent(JSON.parse(e.data)); } catch { /* evento non valido */ } };
      sock.onclose = () => {
        setConnected(false); clearInterval(ping);
        if (!stop) { retry += 1; timer = setTimeout(connect, Math.min(15000, 1000 * 2 ** Math.min(retry, 4))); }
      };
    };
    connect();
    return () => { stop = true; clearTimeout(timer); clearInterval(ping); ws.current?.close(); };
  }, [user, qc, onEvent]);

  useEffect(() => {
    const id = setInterval(() => {
      setTyping((t) => {
        const now = Date.now();
        const next = Object.fromEntries(Object.entries(t).map(([cid, us]) => [cid, Object.fromEntries(Object.entries(us).filter(([, v]) => v.until > now))]));
        return JSON.stringify(next) === JSON.stringify(t) ? t : next;
      });
    }, 1500);
    return () => clearInterval(id);
  }, []);

  const sendTyping = useCallback((cid) => {
    const now = Date.now();
    if (ws.current?.readyState !== 1 || now - (lastTyping.current[cid] || 0) < 2500) return;
    lastTyping.current[cid] = now;
    ws.current.send(JSON.stringify({ type: "typing", channel_id: cid }));
  }, []);
  const setActive = useCallback((cid) => { active.current = cid; }, []);

  return <Ctx.Provider value={{ connected, typing, sendTyping, setActive }}>{children}</Ctx.Provider>;
};
